import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import DescriptionIcon from '@mui/icons-material/Description';
import HistoryIcon from '@mui/icons-material/History';
import dayjs from 'dayjs';
import * as Sentry from '@sentry/react';
import { listAuditLogs } from '../../../api/firebase/function/security/auditLogFunction';
import { downloadCsv } from '../../../utils/pattern/fileExportUtils';
import type { AuditLogListItem } from '../../../types/security';
import {
  auditOperationLabel,
  auditOperationLabelFor,
  auditResultLabel,
  auditResult,
  exportEncoding,
  AUDIT_LOG_PAGE_SIZE,
  AUDIT_LOG_EXPORT_MAX,
  AUDIT_LOG_EXPORT_BATCH_SIZE,
  AUDIT_HOT_RETENTION_MONTHS,
} from '../../../constants/enum';
import CustomTextfield from '../../uiParts/form/CustomTextfield';
import CustomSelectField from '../../uiParts/form/CustomSelectField';
import CustomDatePicker from '../../uiParts/form/CustomDatePicker';
import CustomSnackbar from '../../uiParts/form/CustomSnackbar';
import AuditLogColdExportDialog from './AuditLogColdExportDialog';
import {
  gray40,
  gray60,
  gray80,
  gray100,
  primaryButtonBackgroundColor,
  red50,
  tertiaryButtonBackgroundColor,
} from '../../../constants/colors';
import { regularMd, regularSm, regularXs, sectionTitle } from '../../../constants/location';

interface Props {
  /** 代理モード / 運営スタッフ時の対象企業。companyAdmin 自身は未指定でよい */
  companyId?: string;
}

const styles = {
  filterBar: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'flex-end',
    gap: '12px',
    marginBottom: '16px',
  },
  filterItem: {
    minWidth: '160px',
  },
  actions: {
    display: 'flex',
    gap: '12px',
    marginLeft: 'auto',
  },
  // 既存画面標準の buttonLarge（PatternAiOcr.tsx）に高さを合わせる
  actionButton: {
    height: '44px',
    minWidth: '96px',
    padding: '16px',
  },
  errorText: {
    ...regularSm,
    color: red50,
    marginBottom: '12px',
  },
  // 空状態は Master.tsx / Dictionary.tsx と同じ アイコン + タイトル + 説明 の3点構成
  emptyWrapper: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center' as const,
  },
  emptyIcon: {
    width: '56px',
    height: '56px',
    margin: '80px 0 24px 0',
    '@media screen and (max-width: 600px)': {
      margin: '24px 0 24px 0',
    },
  },
  emptyTitle: {
    whiteSpace: 'pre-wrap',
    mb: 2,
    ...sectionTitle,
    '@media screen and (max-width: 600px)': {
      marginBottom: '12px',
    },
  },
  emptyDescription: {
    whiteSpace: 'pre-wrap',
    mb: 4,
    ...regularMd,
    '@media screen and (max-width: 600px)': {
      marginBottom: '20px',
    },
  },
  table: {
    width: '100%',
  },
  headCell: {
    ...regularXs,
    color: gray80,
    fontWeight: 600,
    borderBottom: '1px solid' + gray40,
    whiteSpace: 'nowrap' as const,
    padding: '8px 12px',
  },
  cell: {
    ...regularSm,
    color: gray100,
    borderBottom: '1px solid' + gray40,
    verticalAlign: 'top',
    padding: '8px 12px',
    wordBreak: 'break-all' as const,
  },
  resultFailure: {
    color: red50,
  },
  loadMoreWrapper: {
    display: 'flex',
    justifyContent: 'center',
    marginTop: '16px',
  },
  hint: {
    ...regularXs,
    color: gray60,
    marginTop: '8px',
  },
};

// 操作フィルタの選択肢（先頭に「すべて」。値は auditOperationKey 形式 "action:resource_type"）
const operationOptions = [
  { option: 'すべて', value: '' },
  ...Object.entries(auditOperationLabel).map(([value, label]) => ({
    option: label,
    value,
  })),
];

// 操作フィルタの値（"action:resource_type"）を Callable 引数へ分解する
const splitOperation = (
  operation: string,
): { action: string | undefined; resource_type: string | undefined } => {
  if (!operation) return { action: undefined, resource_type: undefined };
  const [action, resourceType] = operation.split(':');
  return { action: action || undefined, resource_type: resourceType || undefined };
};

// CSV のヘッダー（表示列と対応）
const CSV_HEADERS = ['日時', '操作者', '操作', '対象ID', '結果', 'IPアドレス', 'User-Agent'];

const formatTimestamp = (iso: string): string =>
  iso ? dayjs(iso).format('YYYY-MM-DD HH:mm:ss') : '';

const toCsvRow = (log: AuditLogListItem): Record<string, string> => ({
  日時: formatTimestamp(log.timestamp),
  操作者: log.user_email,
  操作: auditOperationLabelFor(log.action, log.resource_type),
  対象ID: log.resource_id ?? '',
  結果: auditResultLabel[log.result] ?? log.result,
  IPアドレス: log.ip_address,
  'User-Agent': log.user_agent,
});

/**
 * 監査ログ閲覧タブ（#2985）。設定 > セキュリティ > 監査ログ に表示。
 * 期間 / 操作者メール / 操作 / IP で絞り込み、100 件ずつページ送り。
 * CSV エクスポート（ホット層・最大 AUDIT_LOG_EXPORT_MAX 件）に対応。
 */
const AuditLogTab: React.FC<Props> = ({ companyId }) => {
  const [from, setFrom] = useState<Date | null>(null);
  const [to, setTo] = useState<Date | null>(null);
  const [email, setEmail] = useState('');
  const [operation, setOperation] = useState('');
  const [ip, setIp] = useState('');

  const [logs, setLogs] = useState<AuditLogListItem[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [errorText, setErrorText] = useState('');
  const [hasSearched, setHasSearched] = useState(false);
  const [coldExportOpen, setColdExportOpen] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  // フィルタ条件を Callable 引数へ変換（cursor / page_size は呼び出し側で付与）
  const buildBaseParams = useCallback(
    () => ({
      company_id: companyId,
      from: from ? from.toISOString() : undefined,
      // 終了日は当日の 23:59:59 まで含める
      to: to ? dayjs(to).endOf('day').toISOString() : undefined,
      user_email: email.trim() || undefined,
      ...splitOperation(operation),
      ip_address: ip.trim() || undefined,
    }),
    [companyId, from, to, email, operation, ip],
  );

  const runSearch = useCallback(
    async (cursor: string | null): Promise<void> => {
      setLoading(true);
      setErrorText('');
      try {
        const result = await listAuditLogs({
          ...buildBaseParams(),
          page_size: AUDIT_LOG_PAGE_SIZE,
          cursor,
        });
        setLogs(prev => (cursor ? [...prev, ...result.logs] : result.logs));
        setNextCursor(result.next_cursor);
        setHasSearched(true);
      } catch (e) {
        Sentry.captureException(e);
        setErrorText(e instanceof Error ? e.message : '監査ログの取得に失敗しました。');
      } finally {
        setLoading(false);
      }
    },
    [buildBaseParams],
  );

  // 初回表示および対象企業（代理モード切替）が変わったときに先頭ページを取得する。
  // フィルタ変更では自動検索せず、「検索」ボタンで runSearch を明示的に呼ぶ。
  useEffect(() => {
    void runSearch(null);
  }, [companyId]);

  const handleSearch = (): void => {
    void runSearch(null);
  };

  const handleLoadMore = (): void => {
    if (nextCursor) void runSearch(nextCursor);
  };

  // CSV エクスポート（現在のフィルタ条件でホット層を最大件数まで取得）
  const handleExport = async (): Promise<void> => {
    setExporting(true);
    setErrorText('');
    try {
      const all: AuditLogListItem[] = [];
      let cursor: string | null = null;
      // callable の応答サイズ上限（約10MB）を超えないよう、1 コールあたり
      // AUDIT_LOG_EXPORT_BATCH_SIZE 件ずつ next_cursor が尽きるまで積み上げる
      do {
        const result = await listAuditLogs({
          ...buildBaseParams(),
          page_size: AUDIT_LOG_EXPORT_BATCH_SIZE,
          cursor,
        });
        all.push(...result.logs);
        cursor = result.next_cursor;
      } while (cursor && all.length < AUDIT_LOG_EXPORT_MAX);

      if (all.length === 0) {
        setErrorText('エクスポート対象のログがありません。');
        return;
      }
      // 上限で打ち切られた場合、「全件出力された」ように見えないよう警告する
      if (cursor && all.length >= AUDIT_LOG_EXPORT_MAX) {
        setErrorText(
          `エクスポート上限（${AUDIT_LOG_EXPORT_MAX.toLocaleString()} 件）に達したため、以降のログは出力されていません。期間を分割して再実行してください。`,
        );
      }
      const fileName = `audit_logs_${dayjs().format('YYYYMMDD_HHmmss')}.csv`;
      downloadCsv(all.map(toCsvRow), CSV_HEADERS, fileName, exportEncoding.utf8);
    } catch (e) {
      Sentry.captureException(e);
      setErrorText(e instanceof Error ? e.message : 'CSV エクスポートに失敗しました。');
    } finally {
      setExporting(false);
    }
  };

  return (
    <Box>
      <Box sx={styles.filterBar}>
        <Box sx={styles.filterItem}>
          <CustomDatePicker
            labelText="開始日"
            isRequired={false}
            value={from}
            onChange={(value): void => setFrom(value)}
            width="100%"
            height="44px"
            fontSizeIs14px
            formMb="zero"
            hideHelperText
          />
        </Box>
        <Box sx={styles.filterItem}>
          <CustomDatePicker
            labelText="終了日"
            isRequired={false}
            value={to}
            onChange={(value): void => setTo(value)}
            width="100%"
            height="44px"
            fontSizeIs14px
            formMb="zero"
            hideHelperText
          />
        </Box>
        <Box sx={styles.filterItem}>
          <CustomTextfield
            labelText="操作者（メール）"
            value={email}
            onChange={(e): void => setEmail(e.target.value)}
            inputWidth="100%"
            height="44px"
            fontSizeIs14px
            formMb="0"
          />
        </Box>
        <Box sx={styles.filterItem}>
          <CustomSelectField
            labelText="操作"
            list={operationOptions}
            value={operation}
            onChange={(e): void => setOperation(e.target.value as string)}
            width="100%"
            height="44px"
            formMb="0"
          />
        </Box>
        <Box sx={styles.filterItem}>
          <CustomTextfield
            labelText="IP アドレス"
            value={ip}
            onChange={(e): void => setIp(e.target.value)}
            inputWidth="100%"
            height="44px"
            fontSizeIs14px
            formMb="0"
          />
        </Box>
        <Box sx={styles.actions}>
          <Button
            onClick={(): void => setColdExportOpen(true)}
            disabled={loading || exporting}
            startIcon={<HistoryIcon sx={{ height: '18px', width: '18px' }} />}
            sx={[tertiaryButtonBackgroundColor, styles.actionButton]}
          >
            過去ログ出力
          </Button>
          <Button
            onClick={handleExport}
            disabled={loading || exporting}
            startIcon={
              exporting ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <DownloadIcon sx={{ height: '18px', width: '18px' }} />
              )
            }
            sx={[tertiaryButtonBackgroundColor, styles.actionButton]}
          >
            CSV 出力
          </Button>
          <Button
            onClick={handleSearch}
            disabled={loading || exporting}
            sx={[primaryButtonBackgroundColor, styles.actionButton]}
          >
            検索
          </Button>
        </Box>
      </Box>

      {errorText && <Typography sx={styles.errorText}>{errorText}</Typography>}

      {loading && logs.length === 0 ? (
        <Box sx={styles.loadMoreWrapper}>
          <CircularProgress size={28} />
        </Box>
      ) : hasSearched && logs.length === 0 ? (
        <Box sx={styles.emptyWrapper}>
          <DescriptionIcon sx={styles.emptyIcon} />
          <Typography sx={styles.emptyTitle}>監査ログがありません</Typography>
          <Typography sx={styles.emptyDescription}>期間や条件を変えて検索してください。</Typography>
        </Box>
      ) : (
        <>
          <Box sx={{ overflowX: 'auto' }}>
            <Table sx={styles.table}>
              <TableHead>
                <TableRow>
                  <TableCell sx={styles.headCell}>日時</TableCell>
                  <TableCell sx={styles.headCell}>操作者</TableCell>
                  <TableCell sx={styles.headCell}>操作</TableCell>
                  <TableCell sx={styles.headCell}>対象ID</TableCell>
                  <TableCell sx={styles.headCell}>結果</TableCell>
                  <TableCell sx={styles.headCell}>IP アドレス</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {logs.map(log => (
                  <TableRow key={log.audit_log_id}>
                    <TableCell sx={styles.cell}>{formatTimestamp(log.timestamp)}</TableCell>
                    <TableCell sx={styles.cell}>{log.user_email}</TableCell>
                    <TableCell sx={styles.cell}>
                      {auditOperationLabelFor(log.action, log.resource_type)}
                    </TableCell>
                    <TableCell sx={styles.cell}>{log.resource_id ?? ''}</TableCell>
                    <TableCell
                      sx={[styles.cell, log.result === auditResult.failure && styles.resultFailure]}
                    >
                      {auditResultLabel[log.result] ?? log.result}
                    </TableCell>
                    <TableCell sx={styles.cell}>{log.ip_address}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>

          {nextCursor && (
            <Box sx={styles.loadMoreWrapper}>
              <Button
                onClick={handleLoadMore}
                disabled={loading}
                startIcon={loading ? <CircularProgress size={16} color="inherit" /> : undefined}
                sx={[tertiaryButtonBackgroundColor, styles.actionButton]}
              >
                さらに読み込む
              </Button>
            </Box>
          )}
          <Typography sx={styles.hint}>
            直近 {AUDIT_HOT_RETENTION_MONTHS} ヶ月のログを表示しています（{logs.length}{' '}
            件表示中）。それより前のログは「過去ログ出力」からエクスポートできます。
          </Typography>
        </>
      )}

      <AuditLogColdExportDialog
        open={coldExportOpen}
        onClose={(): void => setColdExportOpen(false)}
        companyId={companyId}
        filters={{
          user_email: email.trim() || undefined,
          ...splitOperation(operation),
          ip_address: ip.trim() || undefined,
        }}
        onSubmitted={(): void => setSnackbarOpen(true)}
      />
      <CustomSnackbar
        open={snackbarOpen}
        onClose={(): void => setSnackbarOpen(false)}
        text=""
        infoText="エクスポートを受け付けました。完了後にダウンロード URL をメールでお送りします。"
      />
    </Box>
  );
};

export default AuditLogTab;
